import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * The page shell every route is built from.
 *
 * Two rules, and between them they are the whole layout:
 *
 * 1. **One scroll region per page.** The app is a fixed-height column — nav at
 *    the top, one route below it — and nothing outside that route scrolls. A
 *    route picks exactly one of `PageScroll` (the page scrolls; used where the
 *    content is prose of unknown length) or `PageFill` (nothing scrolls but a
 *    single panel inside it, which fills to the bottom of the viewport; used
 *    where the content is a table). The pattern that used to be here — a page
 *    that scrolls containing a `max-h-[560px]` box that also scrolls — is the
 *    one arrangement neither a wheel nor a trackpad can address, because which
 *    of the two moves depends on where the pointer happens to be.
 *
 * 2. **Width is the window's.** There is no `max-w` here. The pages are
 *    catalogues and dashboards, and a ten-column table centred in 1152px on a
 *    27-inch monitor is two hundred pixels of table and a thousand of gradient.
 *    Where a line of prose would get too long to track, it is the *text* that
 *    is clamped (`measure` below), not the page — so the width freed up goes
 *    to a second column rather than to a margin.
 *
 * The page header sits outside the scroll region deliberately. The backdrop is
 * a fixed gradient that, in a dark-chrome theme, runs dark at the top and
 * near-white at the bottom, so the heading text is only legible in the top
 * fifth of the viewport; a heading that scrolls is a heading that becomes
 * invisible. Keeping it pinned
 * also means the filters and the table head stay reachable in a long list.
 */

/** The horizontal gutter shared by the header and the body, so they line up. */
export const GUTTER = "px-4 sm:px-6 lg:px-8";

/**
 * A readable line length for body text inside a fluid page.
 *
 * Roughly 75 characters. Wide layout and long lines are different things, and
 * this is what keeps the first from implying the second.
 */
export const MEASURE = "max-w-[75ch]";

/** The fixed-height column a route lives in. */
export function Page({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex h-full min-h-0 flex-col", className)} {...props} />;
}

export interface PageHeaderProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Buttons and controls that belong to the page as a whole, right-aligned. */
  actions?: React.ReactNode;
  /** A back link or breadcrumb, above the title. */
  eyebrow?: React.ReactNode;
  /** Anything that should sit under the title inside the pinned header. */
  children?: React.ReactNode;
}

export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
  children,
  className,
  ...props
}: PageHeaderProps) {
  return (
    <header className={cn("shrink-0 pb-4 pt-5", GUTTER, className)} {...props}>
      {eyebrow && <div className="mb-2 flex items-center gap-2">{eyebrow}</div>}
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3">
        <div className="min-w-0">
          <h1 className="erika-serif text-[22px] leading-tight tracking-tight text-chrome">
            {title}
          </h1>
          {description && (
            <p className={cn("mt-1 text-sm text-chrome/70", MEASURE)}>{description}</p>
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>
      {children}
    </header>
  );
}

/**
 * The body of a page whose content scrolls.
 *
 * `overscroll-contain` stops a flick at the end of the list from turning into
 * a browser back-gesture, which on a trackpad is a single stray pixel away.
 */
export function PageScroll({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain pb-10", GUTTER, className)}
      {...props}
    />
  );
}

/**
 * The body of a page that fills the viewport instead of scrolling.
 *
 * Whatever goes in here is laid out with flexbox and must itself be able to
 * shrink — hence `min-h-0` on the children that matter. A `Panel` is the usual
 * occupant.
 */
export function PageFill({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex min-h-0 flex-1 flex-col pb-5", GUTTER, className)} {...props} />
  );
}

/** A card that fills the height it is given rather than the height of its rows. */
export function Panel({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col rounded-card border border-border bg-card text-card-foreground shadow-panel",
        className,
      )}
      {...props}
    />
  );
}

/**
 * A panel's one scrolling region.
 *
 * This is the scroll region for the whole page when the page is a `PageFill`,
 * which is why it is the only thing in the panel that may overflow: the header,
 * the toolbar and the filter chips above it stay put, so a filter can be
 * changed without scrolling back up to find it.
 */
export function PanelBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("min-h-0 flex-1 overflow-auto overscroll-contain rounded-b-card", className)}
      {...props}
    />
  );
}

/**
 * A panel header: a title on the left, controls on the right.
 *
 * It wraps rather than compressing the controls, because the search box and
 * the export button are the same size on every page and a header that shrinks
 * them at 1100px makes two pages that are meant to look alike look different.
 */
export function PanelHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border px-4 py-2.5",
        className,
      )}
      {...props}
    />
  );
}

export function PanelTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn("erika-serif text-[15px]", className)} {...props} />;
}

/**
 * The side rail of a two-column page.
 *
 * Wide screens get a column of secondary material — filters, a compose form,
 * the audit panels on an answer — beside the primary content rather than
 * stacked a screen and a half below it. Below `xl` it collapses back into the
 * normal flow, first or last depending on which the page wants read first.
 */
export function Rail({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex min-w-0 flex-col gap-4", className)} {...props} />;
}
